/**
 * Step 13 F-1: the one "enter the Agent" rule. Legacy jobs (backend `agent_version: 'legacy'`) keep
 * the old Recruiting AI page; every other job, a job whose response has no `agent_version` yet, and
 * a brand-new conversation (no job) go to the V2 page.
 *
 * Pure (no `@/` runtime imports) so `node --test` can load it directly.
 */
import type { JobAgentVersion } from '@/manage/types/user'

export type { JobAgentVersion }

export const RECRUITING_AI_LEGACY_ROUTE_NAME = 'RecruitingAi'
export const RECRUITING_AI_V2_ROUTE_NAME = 'RecruitingAiV2'

export type AgentRouteName =
  | typeof RECRUITING_AI_LEGACY_ROUTE_NAME
  | typeof RECRUITING_AI_V2_ROUTE_NAME

/** Anything with a job id and (optionally) the backend's `agent_version`. */
export type AgentEntryJob = {
  id?: string | number | null
  agent_version?: JobAgentVersion | string | null
  read_only?: boolean
}

export type AgentEntryQuery = Record<string, string>

/** A named route location, ready for `router.push` / `router.replace` / a redirect. */
export type AgentEntryRoute = {
  name: AgentRouteName
  query: AgentEntryQuery
}

type QueryInput = Record<string, unknown> | null | undefined

const firstQueryValue = (value: unknown) => (Array.isArray(value) ? value[0] : value)

const normalizeQueryValue = (value: unknown) => {
  const picked = firstQueryValue(value)
  return picked === null || picked === undefined ? '' : String(picked).trim()
}

/** Drops empty / nullish values (so `profile: undefined` never shows up as `?profile=`). */
const cleanQuery = (query: QueryInput): AgentEntryQuery => {
  const result: AgentEntryQuery = {}
  if (!query) return result
  for (const [key, value] of Object.entries(query)) {
    const normalized = normalizeQueryValue(value)
    if (normalized) result[key] = normalized
  }
  return result
}

/** Missing or unknown `agent_version` (old API response, job not loaded) counts as V2. */
export const resolveJobAgentVersion = (job: AgentEntryJob | null | undefined): JobAgentVersion =>
  job?.agent_version === 'legacy' ? 'legacy' : 'v2'

export const isLegacyAgentJob = (job: AgentEntryJob | null | undefined) =>
  resolveJobAgentVersion(job) === 'legacy'

/**
 * The Agent page for `job`. `extraQuery` is passed through unchanged (e.g. `profile`, which the old
 * page and the global header use as the job name before the job list loads; the V2 page ignores it).
 * `jobId` always comes from the job and wins over an `extraQuery.jobId`.
 */
export const getAgentEntryRoute = (
  job: AgentEntryJob | null | undefined,
  extraQuery?: QueryInput,
): AgentEntryRoute => {
  const query = cleanQuery(extraQuery)
  delete query.jobId
  const jobId = normalizeQueryValue(job?.id)
  if (!jobId) return { name: RECRUITING_AI_V2_ROUTE_NAME, query }
  return {
    name: isLegacyAgentJob(job) ? RECRUITING_AI_LEGACY_ROUTE_NAME : RECRUITING_AI_V2_ROUTE_NAME,
    query: { ...query, jobId },
  }
}

export const isAgentRouteName = (name: unknown): name is AgentRouteName =>
  name === RECRUITING_AI_LEGACY_ROUTE_NAME || name === RECRUITING_AI_V2_ROUTE_NAME

/** The job id an Agent route (old or V2) is showing; '' for a new conversation or any other route. */
export const getAgentRouteJobId = (route: { name?: unknown; query?: Record<string, unknown> }) =>
  isAgentRouteName(route.name) ? normalizeQueryValue(route.query?.jobId) : ''

/** F-3: the per-row "Talent Recs" action exists only for legacy jobs. */
export const shouldShowTalentRecommendAction = (job: AgentEntryJob | null | undefined) =>
  isLegacyAgentJob(job)
